import { cn } from '@zond/utils';
import { Check, PlusCircle, X } from 'lucide-react';
import * as React from 'react';
import { Badge } from './badge';
import { Button } from './button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from './command';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Separator } from './separator';

interface MultiSelectProps {
  title?: string;
  placeholder?: string;
  selectedValues: Set<string>;
  onSelectedValuesChange: (value: string[]) => void;
  options: {
    label: string;
    value: string;
    icon?: React.ComponentType<{ className?: string }>;
  }[];
  disabled?: boolean;
  clearText?: string;
}

export function MultiSelect({
  title,
  placeholder,
  options,
  selectedValues,
  onSelectedValuesChange,
  disabled,
  clearText = 'Clear filters',
}: MultiSelectProps) {
  const handleRemoveItem = (value: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const newValues = new Set(selectedValues);
    newValues.delete(value);
    onSelectedValuesChange(Array.from(newValues));
  };

  return (
    <Popover>
      <PopoverTrigger asChild disabled={disabled}>
        <Button variant="outline" size="sm" className="h-auto min-h-8 border-dashed py-1">
          <div className="flex items-center w-full">
            <div className="flex items-center shrink-0 mr-1 self-center">
              {!disabled && <PlusCircle className="shrink-0 mr-1" />}
              <span className="shrink-0">{title}</span>
              {selectedValues?.size > 0 && <Separator orientation="vertical" className="mx-2 h-4 shrink-0" />}
            </div>

            {selectedValues?.size > 0 && (
              <div className="flex-1 flex items-center">
                <Badge variant="secondary" className="rounded-sm px-1 font-normal lg:hidden shrink-0">
                  {selectedValues.size}
                </Badge>
                <div className="hidden flex-wrap gap-1 lg:flex">
                  {selectedValues.size > 3 ? (
                    <Badge variant="secondary" className="rounded-sm px-1 font-normal shrink-0">
                      {selectedValues.size} selected
                    </Badge>
                  ) : (
                    options
                      .filter((option) => selectedValues.has(option.value))
                      .map((option) => (
                        <Badge
                          variant="secondary"
                          key={option.value}
                          className="rounded-sm px-1 font-normal my-0.5 group relative hover:bg-secondary/80"
                        >
                          <span>{option.label}</span>
                          <span
                            className="ml-1 cursor-pointer opacity-50 group-hover:opacity-100 transition-opacity inline-flex items-center justify-center"
                            onClick={(e) => handleRemoveItem(option.value, e)}
                          >
                            <X className="size-3" />
                            <span className="sr-only">Remove</span>
                          </span>
                        </Badge>
                      ))
                  )}
                </div>
              </div>
            )}
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[200px] p-0 pointer-events-auto"
        align="start"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => {
          e.stopPropagation();
          e.preventDefault();
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Command>
          <CommandInput placeholder={placeholder} />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selectedValues.has(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => {
                      if (isSelected) {
                        selectedValues.delete(option.value);
                      } else {
                        selectedValues.add(option.value);
                      }
                      const filterValues = Array.from(selectedValues);
                      onSelectedValuesChange(filterValues);
                    }}
                  >
                    <div
                      className={cn(
                        'mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary',
                        isSelected ? 'bg-primary text-primary-foreground' : 'opacity-50 [&_svg]:invisible'
                      )}
                    >
                      <Check />
                    </div>
                    {option.icon && <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />}
                    <span>{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {selectedValues.size > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => onSelectedValuesChange([])}
                    className="justify-center text-center"
                  >
                    {clearText}
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
